import React from 'react';
import { useImmerReducer } from 'use-immer';
import slugify from 'slugify';
import range from 'lodash/range';
import flatten from 'lodash/flatten';
import moment from 'moment';
import { FaExclamationCircle, FaMoneyCheckAlt } from 'react-icons/fa';
import {
	Accordion,
	Box,
	Button,
	CloseButton,
	Dialog,
	Flex,
	Heading,
	HStack,
	Input,
	NativeSelect,
	NumberInput,
	Portal,
	Spacer,
	Switch,
	Table,
	Text,
	Textarea,
	Field,
	Icon
} from '@chakra-ui/react';
import theme from '../theme';
import { roles } from '../lib/generics';
import SignaturePad from '../components/SignaturePad';
import MultiRef from 'react-multi-ref';

const getCrewMembers = (
	{
		project_number = '',
		phase_number = '',
		employee_crew_members = [],
		non_employee_crew_members = [],
		crew_members = [],
		time_items = [],
		per_diems
	},
	prevCrewMembers = []
) =>
	[...employee_crew_members, ...non_employee_crew_members, ...crew_members]
		.filter(
			cm =>
				cm.employee_crew_member_name ||
				cm.non_employee_crew_member_name ||
				cm.crew_member_name ||
				cm.employee_crew_member_role ||
				cm.non_employee_crew_member_role ||
				cm.crew_member_role
		)
		.map(cm => {
			const pnPrefix = project_number.split('-').shift();
			let perDiemVals = {};

			let prevCrewMember = prevCrewMembers.find(
				prevCrewMember =>
					cm.employee_crew_member_name?.toLowerCase() === prevCrewMember.name?.toLowerCase() ||
					cm.non_employee_crew_member_name?.toLowerCase() === prevCrewMember.name?.toLowerCase() ||
					cm.crew_member_name?.toLowerCase() === prevCrewMember.name?.toLowerCase()
			);

			if (prevCrewMember) {
				if (!Object.prototype.hasOwnProperty.call(prevCrewMember, 'name')) {
					prevCrewMember.name =
						cm.employee_crew_member_name || cm.non_employee_crew_member_name || cm.crew_member_name;
				}

				prevCrewMember.role =
					cm.employee_crew_member_role || cm.non_employee_crew_member_role || cm.crew_member_role;

				// Convert existing crew member time item hours into proper objects if not already set
				Object.entries(prevCrewMember.timelogs)
					.filter(
						([, timelog]) =>
							!timelog ||
							typeof timelog === 'number' ||
							(typeof timelog === 'object' && !Object.prototype.hasOwnProperty.call(timelog, 'hours')) ||
							(typeof timelog === 'object' &&
								Object.prototype.hasOwnProperty.call(timelog, 'hours') &&
								timelog.hours === null)
					)
					.forEach(([code, hours = 0]) => {
						code = parseInt(code);

						let timeItem = time_items.find(timeItem => timeItem.code === code);

						if (!prevCrewMember.timelogs[code] || typeof prevCrewMember.timelogs[code] !== 'object') {
							prevCrewMember.timelogs[code] = {
								...timeItem,
								hours,
								phase_number: timeItem?.phase_number || phase_number
							};
						}
					});

				Object.values(prevCrewMember.timelogs).forEach(pcm => {
					if (!time_items.find(({ key }) => key === pcm.key)) {
						if (pcm.key) {
							delete prevCrewMember.timelogs[pcm.key];
						} else {
							delete prevCrewMember.timelogs[pcm.code];
						}
					}

					Object.values(time_items).forEach(ti => {
						if (!Object.prototype.hasOwnProperty.call(prevCrewMember.timelogs, ti.key)) {
							prevCrewMember.timelogs[ti.key] = {
								...ti,
								hours: prevCrewMember.timelogs?.[ti.code]?.hours || 0,
								phase_number:
									prevCrewMember.timelogs?.[ti.code]?.phase_number || ti.phase_number || phase_number,
								task: prevCrewMember.timelogs?.[ti.code]?.task || ti.task || ''
							};
						} else {
							prevCrewMember = {
								...prevCrewMember,
								timelogs: {
									...prevCrewMember.timelogs,
									[ti.key]: {
										...prevCrewMember.timelogs[ti.key],
										onsite: ti.onsite,
										main: ti.main
									}
								}
							};
						}
					});
				});
			} else if (per_diems) {
				perDiemVals.breakfast = per_diems.breakfast_cost;
				perDiemVals.lunch = per_diems.lunch_cost;
				perDiemVals.dinner = per_diems.dinner_cost;
			} else if (Object.prototype.hasOwnProperty.call(window.appData.per_diem_settings, pnPrefix)) {
				perDiemVals = window.appData.per_diem_settings[pnPrefix];
			} else {
				perDiemVals.breakfast = '9.00';
				perDiemVals.lunch = '11.00';
				perDiemVals.dinner = '20.00';
			}

			return (
				prevCrewMember ?? {
					name: cm.employee_crew_member_name || cm.non_employee_crew_member_name || cm.crew_member_name,
					role: cm.employee_crew_member_role || cm.non_employee_crew_member_role || cm.crew_member_role,
					total_hours: 0,
					total_onsite_hours: 0,
					overnight: per_diems?.overnight ?? false,
					breakfast: null,
					breakfast_cost: perDiemVals.breakfast,
					lunch: null,
					lunch_cost: perDiemVals.lunch,
					dinner: null,
					dinner_cost: perDiemVals.dinner,
					timelogs: time_items.reduce(
						(acc, timeItem) => ({
							[`${timeItem.key}`]: {
								...timeItem,
								hours: 0,
								phase_number: timeItem?.phase_number ?? phase_number
							},
							...acc
						}),
						{}
					)
				}
			);
		});

const roundHours = hours => {
	// Fractional hour
	hours = hours / 60;

	// Twig eqiuvalent: round(2, 'ceil')
	if (hours < Math.floor(hours) + 0.25) {
		hours = Math.floor(hours);
	} else if (hours >= Math.floor(hours) && hours >= Math.floor(hours) + 0.25 && hours < Math.floor(hours) + 0.75) {
		// If hours is > floor'd hours but less than nearest half, round hours to nearest 0.5
		hours = Math.floor(hours) + 0.5;
	} else if (hours <= Math.ceil(hours) && hours >= Math.floor(hours) + 0.75) {
		// Otherwise if hours is < ceil'd hours but greater than nearest half, round hours up to nearest whole #
		hours = Math.ceil(hours);
	}

	return hours;
};

const formatTimeValue = hours => `${roundHours(hours).toFixed(2)} hrs`;

const validateTime = time => {
	if (isNaN(Number(time))) {
		return false;
	}

	if (typeof time !== 'string') {
		time = JSON.stringify(time);
	}

	if (time < 0 || time > 2400 || time[2] > 5) {
		return false;
	}

	return time;
};

function getTotalHours(hours) {
	// 60000 ms in 1 min
	const minToMs = 60000;
	const currentDay = moment('0000', 'HHmm').dayOfYear();

	const _s1a = hours.shop_1.arrive ? moment(hours.shop_1.arrive, 'HHmm').dayOfYear(currentDay) : '';
	let _s1l = hours.shop_1.leave ? moment(hours.shop_1.leave, 'HHmm').dayOfYear(currentDay) : '';
	let _s2a = hours.shop_2.arrive ? moment(hours.shop_2.arrive, 'HHmm').dayOfYear(currentDay) : '';
	let _s2l = hours.shop_2.leave ? moment(hours.shop_2.leave, 'HHmm').dayOfYear(currentDay) : '';
	let _sa = hours.site.arrive ? moment(hours.site.arrive, 'HHmm').dayOfYear(currentDay) : '';
	let _sl = hours.site.leave ? moment(hours.site.leave, 'HHmm').dayOfYear(currentDay) : '';

	_s1l = _s1l && _s1l < _s1a ? _s1l.add(1, 'd') : _s1l;
	_s2a = _s2a && _s2a < _s1a ? _s2a.add(1, 'd') : _s2a;
	_s2l = _s2l && _s2l < _s1a ? _s2l.add(1, 'd') : _s2l;
	_sa = _sa && _sa < _s1a ? _sa.add(1, 'd') : _sa;
	_sl = _sl && _sl < _s1a ? _sl.add(1, 'd') : _sl;

	let s1loadUnloadTime;
	if (!_s1a || !_s1l) {
		s1loadUnloadTime = 0;
	} else if (_s1a.isAfter(_s1l)) {
		_s1l.dayOfYear(currentDay + 1);
		s1loadUnloadTime = _s1l.diff(_s1a);
		s1loadUnloadTime = s1loadUnloadTime < 0 ? 0 : s1loadUnloadTime;
	} else {
		s1loadUnloadTime = _s1l - _s1a;
		s1loadUnloadTime = s1loadUnloadTime < 0 ? 0 : s1loadUnloadTime;
	}

	let s2loadUnloadTime;
	if (!_s2a || !_s2l) {
		s2loadUnloadTime = 0;
	} else if (_s2a.isAfter(_s2l)) {
		_s2l.dayOfYear(currentDay + 1);
		s2loadUnloadTime = _s2l.diff(_s2a);
	} else {
		s2loadUnloadTime = _s2l - _s2a;
	}

	let s1travelTime;
	if (!_s1l || !_sa) {
		s1travelTime = 0;
	} else if (_s1l.isAfter(_sa)) {
		_sa.dayOfYear(currentDay + 1);
		s1travelTime = _sa.diff(_s1l);
	} else {
		s1travelTime = _sa - _s1l;
	}

	let s2travelTime;
	if (!_sl || !_s2a) {
		s2travelTime = 0;
	} else if (_sl.isAfter(_s2a)) {
		_s2a.dayOfYear(currentDay + 1);
		s2travelTime = _s2a.diff(_sl);
	} else {
		s2travelTime = _s2a - _sl;
	}

	let siteTime;
	if (!_sa || !_sl) {
		siteTime = 0;
	} else if (_sa.isAfter(_sl)) {
		_sl.dayOfYear(currentDay + 1);
		siteTime = _sl.diff(_sa);
	} else {
		siteTime = _sl - _sa;
	}

	const loadUnloadTime = (s1loadUnloadTime + s2loadUnloadTime) / minToMs;
	const travelTime = (s1travelTime + s2travelTime) / minToMs;
	siteTime /= minToMs;

	const perDiemTimes = window.appData.per_diem_times;

	const all = _s1l === moment('0000', 'HHmm') && _s2a === moment('0000', 'HHmm');
	const breakfast =
		_s1l > moment('0000', 'HHmm') &&
		_s1l < moment(validateTime(perDiemTimes?.breakfast?.departure) || '0600', 'HHmm');
	const lunch =
		_s1l > moment('0000', 'HHmm') &&
		_s1l <= moment(validateTime(perDiemTimes?.lunch?.departure) || '1100', 'HHmm') &&
		_s2a >= moment(validateTime(perDiemTimes?.lunch?.arrival) || '1400', 'HHmm');
	const dinner = _s2a > moment(validateTime(perDiemTimes?.dinner?.departure) || '1930', 'HHmm');

	return { loadUnloadTime, travelTime, siteTime, all, breakfast, lunch, dinner };
}

const isValidTimeEntry = value => {
	if (isNaN(Number(value))) {
		return false;
	}

	const index = String(value).length - 1;
	switch (index) {
		case 0:
			return value[index] <= 2;
		case 1:
			return value[index - 1] === 2 ? value[index] <= 4 : true;
		case 2:
			return value[index] <= 5 && value <= 240;
		case 3:
			return value <= 2400;
		default:
			return false;
	}
};

// Create time log hour ticker options
const timeLogHours = [
	...range(0, 1440, 30).map(value => ({
		label: formatTimeValue(value),
		value
	}))
];

function formReducer(draft, action) {
	switch (action.type) {
		case 'add_equipment_item': {
			const { name, billable, rental, unit, task, phase, measurement } = action.payload;

			const key = slugify(name, {
				replacement: '_',
				lower: true,
				remove: /[*+~.()'"!:@]/g
			});

			draft.fields.equipment.push({
				key,
				title: name,
				quantity: measurement === 'days' ? '1' : '',
				billable: billable ?? true,
				rental: rental ?? false,
				unit: unit ?? '',
				task,
				phase,
				measurement
			});

			break;
		}
		case 'remove_equipment_item': {
			const { index } = action.payload;

			draft.fields.equipment.splice(index, 1);

			break;
		}
		case 'update_equipment_item': {
			const { field, index, value } = action.payload;
			if (field === 'phase') {
				draft.fields.equipment[index][field] = value;
				draft.fields.equipment[index].task = '';
			} else if (field === 'mileage_start' || field === 'mileage_end') {
				draft.fields.equipment[index][field] = value;

				if (
					Object.prototype.hasOwnProperty.call(draft.fields.equipment[index], 'mileage_start') &&
					Object.prototype.hasOwnProperty.call(draft.fields.equipment[index], 'mileage_end')
				) {
					draft.fields.equipment[index].quantity = Math.abs(
						parseInt(draft.fields.equipment[index].mileage_start) -
							parseInt(draft.fields.equipment[index].mileage_end)
					).toString();
				}
			} else {
				draft.fields.equipment[index][field] = value;
			}

			break;
		}
		case 'sync_equipment_items': {
			const { loadoutData, loadoutDefinition, handoffData, unitTable } = action.payload;

			const equipment = flatten(
				Object.entries(loadoutData)
					.filter(([categoryKey, category]) => categoryKey && category)
					.map(([categoryKey, category = {}]) => {
						// Groups are "items" generated from/related to subcategories themselves
						let groups = [];

						if (category.unit) {
							const hasCheck = Object.values(category).find(
								value =>
									Object.prototype.hasOwnProperty.call(value, 'part_one') &&
									(value.part_one.checked || value.part_two.checked) &&
									value.unit === category.unit
							);

							if (hasCheck) {
								const measurement = unitTable.find(({ unit }) => category.unit === unit)?.plural_label;
								const quantity = measurement === 'days' ? '1' : '';

								groups.push({
									key: categoryKey,
									title: categoryKey
										.split('_')
										.map(part => part.charAt(0).toUpperCase() + part.slice(1))
										.join(' '),
									quantity,
									rental: false,
									unit: category.unit,
									measurement,
									billable: true,
									task: handoffData?.units?.task ?? '',
									phase: handoffData?.units?.phase_number ?? handoffData?.phase_number ?? '',
									mileage_start: null,
									mileage_end: null
								});
							}
						}

						const items = flatten(
							Object.entries(category).map(([itemKey, item]) => {
								let items = [];

								if (itemKey === 'addons' && itemKey.length > 0) {
									items = item
										.filter(
											addon =>
												addon.name &&
												addon.billable &&
												addon.unit_code &&
												(addon.part_one.checked || addon.part_two.checked)
										)
										.map(addon => ({
											key: slugify(addon.name, {
												replacement: '_',
												lower: true,
												remove: /[*+~.()'"!:@]/g
											}),
											title: addon.name,
											quantity: '',
											rental: false,
											unit: addon.unit_code,
											billable: true,
											task: handoffData?.units?.task ?? '',
											phase: handoffData?.units?.phase_number ?? handoffData?.phase_number ?? '',
											mileage_start: null,
											mileage_end: null
										}));
								} else if (
									(item.billable || item.unit) &&
									(item.part_one.checked || item.part_two.checked) &&
									!groups.find(group => group.unit === item.unit)
								) {
									const measurement = unitTable.find(({ unit }) => item.unit === unit)?.plural_label;
									const quantity = measurement === 'days' ? (item.part_two.checked ? '1' : '') : '';

									items.push({
										key: itemKey,
										title: loadoutDefinition[categoryKey]?.[itemKey]?.['#title'],
										quantity,
										unit: item.unit ?? '',
										rental: false,
										billable: true,
										task: handoffData?.units?.task ?? '',
										phase: handoffData?.units?.phase_number ?? handoffData?.phase_number ?? '',
										measurement,
										mileage_start: null,
										mileage_end: null
									});
								}

								return items;
							})
						).filter(Boolean);

						return [...groups, ...items];
					})
					.filter(({ length }) => length > 0)
			);

			equipment.forEach(equip => {
				const exists = draft.fields.equipment.find(({ key }) => key === equip.key);

				if (!exists) {
					draft.fields.equipment.push(equip);
				}
			});

			break;
		}
		case 'save_signature': {
			const { signatureData, signatureType } = action.payload;
			draft.fields[`${signatureType}_signature`] = signatureData;

			break;
		}
		case 'update_field_value': {
			const { fieldName, fieldValue } = action.payload;
			draft.fields[fieldName] = fieldValue;

			break;
		}
		case 'set_time_data': {
			const {
				index = null,
				field = '',
				key = '',
				setName = '',
				setType = '',
				value = null,
				timeItems
			} = action.payload;

			const prevHours = draft.fields.hours.total;

			// If we're changing arrive/leave shop/site
			if (setName && setType) {
				draft.fields.hours[setName][setType] = value;
			}

			// If we're changing phase/task tickers
			if (key && field) {
				draft.fields.crew_members[index].timelogs[key][field] = value;
			}

			const { loadUnloadTime, travelTime, siteTime, all, breakfast, lunch, dinner } = getTotalHours(
				!draft.fields.hours.site.arrive && !draft.fields.hours.site.leave
					? {
							...draft.fields.hours,
							site: {
								arrive: draft.fields.hours.shop_1.leave,
								leave: draft.fields.hours.shop_1.leave
							}
						}
					: draft.fields.hours
			);

			draft.fields.hours.total =
				(roundHours(loadUnloadTime) + roundHours(travelTime) + roundHours(siteTime)) * 60;

			// Check if we're changing individual tickers from disabled calculations
			if (typeof index === 'number' && draft.fields.calc_disabled) {
				draft.fields.crew_members[index].total_onsite_hours = Object.entries(
					draft.fields.crew_members[index].timelogs
				)
					.filter(
						([key]) =>
							// Automatically set everything marked as Onsite except Travel, Load/Unload,
							// and Diving, Floating, and Wading into Onsite Total hours
							timeItems.find(({ key: timeItemKey }) => key === timeItemKey)?.onsite &&
							key !== '110-1' &&
							key !== '102-1' &&
							key !== '100-1'
					)
					.reduce((acc, [, { hours }]) => acc + hours, 0);

				draft.fields.crew_members[index].total_hours = Object.values(
					draft.fields.crew_members[index].timelogs
				).reduce((acc, { hours }) => acc + hours, 0);
			} else if (!draft.fields.calc_disabled) {
				// Onsite Main item
				const onsiteMainItem = timeItems.find(({ main } = false) => main === true);

				// Otherwise, sync the crew members from either re-calculation or screen swapping
				draft.fields.crew_members.forEach(cm => {
					if (setName && setType) {
						// If we're changing arive/leave shop/site...
						cm.breakfast = cm.overnight ? all || breakfast : false;
						cm.lunch = cm.overnight ? all || lunch : false;
						cm.dinner = cm.overnight ? all || dinner : false;
					} else if (prevHours === draft.fields.hours.total) {
						// Unsure about this case
						cm.breakfast = cm.breakfast ?? (all || breakfast);
						cm.lunch = cm.lunch ?? (all || lunch);
						cm.dinner = cm.dinner ?? (all || dinner);
					} else {
						// Otherwise, re-calculate any crew member changes if no existing data
						cm.breakfast = cm.overnight ? (cm.breakfast ?? (all || breakfast)) : false;
						cm.lunch = cm.overnight ? (cm.lunch ?? (all || lunch)) : false;
						cm.dinner = cm.overnight ? (cm.dinner ?? (all || dinner)) : false;
					}

					// ⚠️ WARNING: SENSITIVE AREA ⚠️

					Object.keys(cm.timelogs)
						.filter(timeLog => timeLog !== '110-1')
						.forEach(timeLog => {
							cm.timelogs[timeLog].hours = 0;
						});

					if (onsiteMainItem) {
						// NOTE: Onsite main is the set of (filtered by Onsite Item switch enabled)
						// 100, 102, and (103 or 603) labor code hours combined
						let onsiteMainTime = 0;

						// If an item is an onsite item
						if (cm.timelogs['100-1']?.onsite) {
							cm.timelogs['100-1'].hours = 0;
							// Reset the item and load the time into Onsite Main time
							onsiteMainTime = loadUnloadTime;
						} else if (cm.timelogs['100-1']) {
							// Otherwise, assign it
							cm.timelogs['100-1'].hours = loadUnloadTime;
						} else {
							// Otherwise if deleted, load into Onsite Main time
							onsiteMainTime = loadUnloadTime;
						}

						if (cm.timelogs['102-1']?.onsite) {
							cm.timelogs['102-1'].hours = 0;
							onsiteMainTime += travelTime;
						} else if (cm.timelogs['102-1']) {
							cm.timelogs['102-1'].hours = travelTime;
						} else {
							onsiteMainTime += travelTime;
						}

						if (cm.timelogs['103-1']?.onsite) {
							cm.timelogs['103-1'].hours = 0;
							onsiteMainTime += siteTime;
						} else if (cm.timelogs['603-1']?.onsite) {
							cm.timelogs['603-1'].hours = 0;
							onsiteMainTime += siteTime;
						} else if (cm.timelogs['103-1']) {
							cm.timelogs['103-1'].hours = siteTime;
						} else if (cm.timelogs['603-1']) {
							cm.timelogs['603-1'].hours = siteTime;
						}

						if (cm.timelogs['110-1']?.onsite && cm.timelogs['110-1']?.hours > 0) {
							// Subtract Dive Log hours from Onsite Main time
							onsiteMainTime -= cm.timelogs['110-1'].hours;
						}

						cm.timelogs[onsiteMainItem.key].hours = onsiteMainTime;
						cm.total_onsite_hours = Object.values(cm.timelogs)
							.filter(({ onsite }) => onsite)
							.reduce((acc, { hours }) => acc + hours, 0);
					} else {
						if (cm.timelogs['100-1']) {
							cm.timelogs['100-1'].hours = loadUnloadTime;
						}

						if (cm.timelogs['102-1']) {
							cm.timelogs['102-1'].hours = travelTime;
						}

						if (cm.timelogs['103-1']) {
							cm.timelogs['103-1'].hours = siteTime;
							cm.total_onsite_hours = siteTime;
						} else if (cm.timelogs['603-1']) {
							cm.timelogs['603-1'].hours = siteTime;
							cm.total_onsite_hours = siteTime;
						}
					}

					cm.total_hours = Object.values(cm.timelogs).reduce(
						(acc, { hours }) => acc + roundHours(hours) * 60,
						0
					);
				});
			}

			break;
		}
		case 'update_crew_member': {
			const { field, index, value } = action.payload;
			draft.fields.crew_members[index][field] = value;

			break;
		}
		case 'add_subconsultant': {
			draft.fields.subconsultants.push({
				name: '',
				details: ''
			});

			break;
		}
		case 'remove_subconsultant': {
			const { index } = action.payload;
			draft.fields.subconsultants.splice(index, 1);
			break;
		}
		case 'update_subconsultant': {
			const { field, value, index } = action.payload;
			draft.fields.subconsultants[index][field] = value;
			break;
		}
		default:
			break;
	}
}

export default ({ currentForm, edit, handoffData, formData, dataAPI, saveForm, currentPacket, isSubmitted }) => {
	const [loadoutList, setLoadoutList] = React.useState(false);
	const [loadoutKey, setLoadoutKey] = React.useState({});
	const [removeSubConsultantDialogOpen, setRemoveSubConsultantDialogOpen] = React.useState(null);
	const [removeEquipmentItemDialogOpen, setRemoveEquipmentItemDialogOpen] = React.useState(null);
	const [disableCalculationsDialogOpen, setDisableCalculationsDialogOpen] = React.useState(false);
	const [calculationsDisabled, setCalculationsDisabled] = React.useState();
	const [equipmentItem, setEquipmentItem] = React.useState({
		name: '',
		rental: false,
		billable: true,
		unit: ''
	});

	const autoCalculateModalRef = React.useRef();
	const [removeEquipmentRefs] = React.useState(() => new MultiRef());
	const [removeSubconsultantRefs] = React.useState(() => new MultiRef());

	const loadoutData = dataAPI.getFormData('loadout_list');
	const loadoutDefinition = dataAPI.getFormDefinition('loadout_list');
	const projectNumber = dataAPI.getPhaseValue('project_number');

	const projectPhases = React.useMemo(
		() => window.projectList.filter(({ project_number }) => projectNumber === project_number),
		[window.projectList, projectNumber]
	);

	const [state, dispatch] = useImmerReducer(formReducer, {
		fields: {
			...formData,
			crew_members: getCrewMembers(handoffData, formData.crew_members),
			time_items: handoffData.time_items,
			get supervisor_name() {
				return (
					formData.supervisor_name ||
					this.crew_members.find(({ role }) => role === 'dive_supervisor' || role === 'field_supervisor')
						?.name ||
					''
				);
			}
		}
	});

	const unitTable = React.useMemo(() => {
		const result = dataAPI.getPhaseValue('unit_table');
		return Array.isArray(result) ? result.sort((a, b) => (a.name < b.name ? -1 : 1)) : [];
	}, [dataAPI]);

	const loadoutListItems = React.useMemo(
		() =>
			flatten(
				Object.entries(loadoutData)
					.filter(([categoryKey, category]) => categoryKey && category)
					.map(([categoryKey, category = {}]) =>
						flatten(
							Object.entries(category).map(([itemKey, item]) => {
								if (itemKey === 'addons' && item.length > 0) {
									return item
										.filter(
											addon =>
												addon.name &&
												(addon.billable || addon.unit) &&
												(addon.part_one.checked || addon.part_two.checked)
										)
										.map(({ name: title, unit_code: unit, billable }) => ({
											key: slugify(title, {
												replacement: '_',
												lower: true,
												remove: /[*+~.()'"!:@]/g
											}),
											title,
											quantity: '',
											rental: false,
											unit,
											billable
										}));
								} else if (
									Object.prototype.hasOwnProperty.call(item, 'billable') &&
									item.billable === true &&
									Object.prototype.hasOwnProperty.call(item, 'part_one') &&
									Object.prototype.hasOwnProperty.call(item, 'part_two') &&
									loadoutDefinition[categoryKey]
								) {
									return {
										key: itemKey,
										title: loadoutDefinition[categoryKey]?.[itemKey]?.['#title'] ?? '',
										quantity: '',
										unit: item.unit ?? '',
										billable: true,
										rental: false
									};
								}
							})
						).filter(Boolean)
					)
					.filter(({ length }) => length > 0)
			),
		[loadoutData]
	);

	React.useEffect(() => {
		if (state.fields.crew_members) {
			// Auto calculate time data from screen swapping,
			// and after handoff data sync from Project Information
			dispatch({
				type: 'set_time_data',
				payload: { timeItems: handoffData.time_items }
			});
		}

		dispatch({
			type: 'sync_equipment_items',
			payload: { loadoutData, loadoutDefinition, handoffData, unitTable }
		});
	}, []);

	React.useEffect(() => {
		saveForm({
			...currentForm,
			form_data: state.fields
		});
	}, [state.fields]);

	return (
		<>
			<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
				<Text as="label" fontSize={16} pb="4px">
					Description of Work Performed
				</Text>
				<Field.Root invalid={!state.fields.description_of_work}>
					<Textarea
						fontSize={16}
						bgColor={theme.colors.white}
						value={state.fields.description_of_work}
						onChange={({ target }) => {
							dispatch({
								type: 'update_field_value',
								payload: {
									fieldName: 'description_of_work',
									fieldValue: target.value
								}
							});
						}}
						disabled={isSubmitted && !edit}
						_disabled={{
							opacity: 1,
							bgColor: theme.colors.gray[400],
							color: theme.colors.black
						}}
					/>
				</Field.Root>
			</Box>
			<Box>
				<Heading fontSize={24} pt="24px" pb="12px" color={theme.colors.blue[700]} ml="8px">
					Hours
				</Heading>
			</Box>
			<Flex>
				<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Text as="label" fontSize={16} pb="4px">
						Arrive Shop
					</Text>
					<Field.Root
						invalid={
							state.fields.hours.shop_1.arrive &&
							(state.fields.hours.shop_1.arrive.length < 4 || state.fields.hours.shop_1.arrive > 2400)
						}
					>
						<Input
							fontSize={16}
							value={state.fields.hours.shop_1.arrive || ''}
							onChange={({ target }) => {
								if (!target.value || isValidTimeEntry(target.value)) {
									dispatch({
										type: 'set_time_data',
										payload: {
											setName: 'shop_1',
											setType: 'arrive',
											value: target.value,
											timeItems: handoffData.time_items
										}
									});
								}
							}}
							disabled={isSubmitted && !edit}
							_disabled={{
								opacity: 1,
								bgColor: theme.colors.gray[400],
								color: theme.colors.black
							}}
							bgColor={theme.colors.white}
							borderRadius="base"
							placeholder="----"
						/>
					</Field.Root>
				</Box>
				<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Text as="label" fontSize={16} pb="4px">
						Leave Shop/Hotel
					</Text>
					<Field.Root
						invalid={
							state.fields.hours.shop_1.leave &&
							(state.fields.hours.shop_1.leave.length < 4 || state.fields.hours.shop_1.leave > 2400)
						}
					>
						<Input
							fontSize={16}
							bgColor={theme.colors.white}
							borderRadius="base"
							placeholder="----"
							value={state.fields.hours.shop_1.leave || ''}
							onChange={({ target }) => {
								if (!target.value || isValidTimeEntry(target.value)) {
									dispatch({
										type: 'set_time_data',
										payload: {
											setName: 'shop_1',
											setType: 'leave',
											value: target.value,
											timeItems: handoffData.time_items
										}
									});
								}
							}}
							disabled={isSubmitted && !edit}
							_disabled={{
								opacity: 1,
								bgColor: theme.colors.gray[400],
								color: theme.colors.black
							}}
						/>
					</Field.Root>
				</Box>
			</Flex>
			<Flex>
				<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Text as="label" fontSize={16} pb="4px">
						Arrive Site
					</Text>
					<Field.Root
						invalid={
							state.fields.hours.site.arrive &&
							(state.fields.hours.site.arrive.length < 4 || state.fields.hours.site.arrive > 2400)
						}
					>
						<Input
							fontSize={16}
							bgColor={theme.colors.white}
							borderRadius="base"
							placeholder="----"
							value={state.fields.hours.site.arrive || ''}
							onChange={({ target }) => {
								if (!target.value || isValidTimeEntry(target.value)) {
									dispatch({
										type: 'set_time_data',
										payload: {
											setName: 'site',
											setType: 'arrive',
											value: target.value,
											timeItems: handoffData.time_items
										}
									});
								}
							}}
							disabled={isSubmitted && !edit}
							_disabled={{
								opacity: 1,
								bgColor: theme.colors.gray[400],
								color: theme.colors.black
							}}
						/>
					</Field.Root>
				</Box>
				<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Text as="label" fontSize={16} pb="4px">
						Leave Site
					</Text>
					<Field.Root
						invalid={
							state.fields.hours.site.leave &&
							(state.fields.hours.site.leave.length < 4 || state.fields.hours.site.leave > 2400)
						}
					>
						<Input
							fontSize={16}
							bgColor={theme.colors.white}
							borderRadius="base"
							placeholder="----"
							value={state.fields.hours.site.leave || ''}
							onChange={({ target }) => {
								if (!target.value || isValidTimeEntry(target.value)) {
									dispatch({
										type: 'set_time_data',
										payload: {
											setName: 'site',
											setType: 'leave',
											value:
												target.value?.length > 4 ? state.fields.hours.site.leave : target.value,
											timeItems: handoffData.time_items
										}
									});
								}
							}}
							disabled={isSubmitted && !edit}
							_disabled={{
								opacity: 1,
								bgColor: theme.colors.gray[400],
								color: theme.colors.black
							}}
						/>
					</Field.Root>
				</Box>
			</Flex>
			<Flex>
				<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Text as="label" fontSize={16} pb="4px">
						Arrive Shop/Hotel
					</Text>
					<Field.Root
						invalid={
							state.fields.hours.shop_2.arrive &&
							(state.fields.hours.shop_2.arrive.length < 4 || state.fields.hours.shop_2.arrive > 2400)
						}
					>
						<Input
							fontSize={16}
							bgColor={theme.colors.white}
							borderRadius="base"
							placeholder="----"
							value={state.fields.hours.shop_2.arrive || ''}
							onChange={({ target }) => {
								if (!target.value || isValidTimeEntry(target.value)) {
									dispatch({
										type: 'set_time_data',
										payload: {
											setName: 'shop_2',
											setType: 'arrive',
											value: target.value,
											timeItems: handoffData.time_items
										}
									});
								}
							}}
							disabled={isSubmitted && !edit}
							_disabled={{
								opacity: 1,
								bgColor: theme.colors.gray[400],
								color: theme.colors.black
							}}
						/>
					</Field.Root>
				</Box>
				<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
					<Text as="label" fontSize={16} pb="4px">
						Leave Shop
					</Text>
					<Field.Root
						invalid={
							state.fields.hours.shop_2.leave &&
							(state.fields.hours.shop_2.leave.length < 4 || state.fields.hours.shop_2.leave > 2400)
						}
					>
						<Input
							fontSize={16}
							bgColor={theme.colors.white}
							borderRadius="base"
							placeholder="----"
							value={state.fields.hours.shop_2.leave || ''}
							onChange={({ target }) => {
								if (!target.value || isValidTimeEntry(target.value)) {
									dispatch({
										type: 'set_time_data',
										payload: {
											setName: 'shop_2',
											setType: 'leave',
											value: target.value,
											timeItems: handoffData.time_items
										}
									});
								}
							}}
							disabled={isSubmitted && !edit}
							_disabled={{
								opacity: 1,
								bgColor: theme.colors.gray[400],
								color: theme.colors.black
							}}
						/>
					</Field.Root>
				</Box>
			</Flex>
			<Flex align="center">
				<Flex w="100%" p="8px" align="center">
					<Text as="label" fontSize={16} mr="4px !important" fontWeight="bold">
						Total:{' '}
					</Text>
					<Text as="label" fontSize={16} fontWeight={900} color={theme.colors.blue[700]}>
						{formatTimeValue(state.fields.hours.total)}
					</Text>
				</Flex>
				<Flex w="100%" p="8px" justify="flex-end" align="center">
					<Switch.Root
						mr="4px !important"
						size="lg"
						disabled={isSubmitted && !edit}
						checked={state.fields.calc_disabled}
						onCheckedChange={() => {
							setDisableCalculationsDialogOpen(true);
							setCalculationsDisabled(!state.fields.calc_disabled);
						}}
					>
						<Switch.HiddenInput />
						<Switch.Control />
					</Switch.Root>
					<Text as="label" fontSize={16}>
						Disable Calculations
					</Text>
					<Dialog.Root
						role="alertdialog"
						initialFocusEl={autoCalculateModalRef}
						onOpenChange={({ open }) => {
							if (!open) {
								setDisableCalculationsDialogOpen(false);
								setCalculationsDisabled(state.fields.calc_disabled);
							}
						}}
						open={disableCalculationsDialogOpen}
						placement="center"
					>
						<Portal>
							<Dialog.Backdrop />
							<Dialog.Positioner>
								<Dialog.Content opacity="1 !important">
									<Dialog.Header>
										<Dialog.Title fontSize="lg" fontWeight="bold">
											Alert!
										</Dialog.Title>
									</Dialog.Header>
									<Dialog.Body>
										<p>
											{calculationsDisabled
												? 'With calculations disabled, changes to hours will no longer update employee time logs or per diems.'
												: 'With calculations enabled, any changes to hours will cause recalculations that could potentially override any manual changes that have been made to employee time logs.'}
										</p>
									</Dialog.Body>
									<Dialog.Footer>
										<Dialog.ActionTrigger asChild>
											<Button
												size="lg"
												ref={autoCalculateModalRef}
												onClick={() => {
													setDisableCalculationsDialogOpen(false);
													setCalculationsDisabled(state.fields.calc_disabled);
													dispatch({
														type: 'update_field_value',
														payload: {
															fieldName: 'calc_disabled',
															fieldValue: state.fields.calc_disabled
														}
													});
												}}
											>
												Cancel
											</Button>
										</Dialog.ActionTrigger>
										<Button
											colorPalette="red"
											size="lg"
											ml={3}
											bg={theme.colors.red[500]}
											color={theme.colors.white}
											onClick={() => {
												setDisableCalculationsDialogOpen(false);
												setCalculationsDisabled(!state.fields.calc_disabled);
												dispatch({
													type: 'update_field_value',
													payload: {
														fieldName: 'calc_disabled',
														fieldValue: !state.fields.calc_disabled
													}
												});
											}}
										>
											Continue
										</Button>
									</Dialog.Footer>
									<Dialog.CloseTrigger asChild>
										<CloseButton size="sm" />
									</Dialog.CloseTrigger>
								</Dialog.Content>
							</Dialog.Positioner>
						</Portal>
					</Dialog.Root>
				</Flex>
			</Flex>
			<Accordion.Root multiple collapsible p="8px" defaultValue={[0, 1]}>
				<Accordion.Item value={0} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Time Logs
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							{state.fields.crew_members.map((cm, cmIndex) => (
								<Box key={`${cm.name}_${cmIndex}`} p="8px">
									<HStack mb="8px">
										<HStack bgColor={theme.colors.gray[500]} width="50%" height="50px" p="8px">
											<Text fontSize={16} fontWeight="900" color={theme.colors.blue[700]}>
												{cmIndex + 1}.
											</Text>
											<Text fontSize={16} as="label">
												Name
											</Text>
											<Text fontSize={16}>{cm.name}</Text>
										</HStack>
										<HStack bgColor={theme.colors.gray[500]} width="50%" height="50px" p="8px">
											<Text fontSize={16} as="label">
												Role
											</Text>
											<Text fontSize={16}>{roles[cm.role]}</Text>
										</HStack>
									</HStack>
									<HStack justify="space-between" align="stretch">
										<HStack bgColor={theme.colors.gray[500]} p="8px" width="25%" borderRadius={2}>
											<Switch.Root
												size="lg"
												disabled={!window.isSupervisor && isSubmitted && !edit}
												onCheckedChange={() => {
													dispatch({
														type: 'update_crew_member',
														payload: {
															field: 'overnight',
															index: cmIndex,
															value: !cm.overnight
														}
													});

													dispatch({
														type: 'set_time_data',
														payload: {
															index: cmIndex,
															timeItems: handoffData.time_items
														}
													});
												}}
												checked={cm.overnight}
											>
												<Switch.HiddenInput />
												<Switch.Control />
											</Switch.Root>
											<Text as="label" fontSize={16}>
												Overnight Stay?
											</Text>
										</HStack>
										<HStack bgColor={theme.colors.gray[500]} p="8px" width="25%" borderRadius={2}>
											<Switch.Root
												size="lg"
												disabled={!window.isSupervisor && isSubmitted && !edit}
												onCheckedChange={() =>
													dispatch({
														type: 'update_crew_member',
														payload: {
															field: 'breakfast',
															index: cmIndex,
															value: !cm.breakfast
														}
													})
												}
												checked={cm.breakfast}
											>
												<Switch.HiddenInput />
												<Switch.Control />
											</Switch.Root>
											<Text as="label" fontSize={16}>
												Breakfast
											</Text>
											<NumberInput.Root
												precision={2}
												value={cm.breakfast_cost || '9.00'}
												onValueChange={e =>
													dispatch({
														type: 'update_crew_member',
														payload: {
															field: 'breakfast_cost',
															index: cmIndex,
															value: e.value
														}
													})
												}
												disabled={isSubmitted && !edit}
											>
												<NumberInput.Input
													fontSize={16}
													bgColor="white"
													borderRadius="base"
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</NumberInput.Root>
										</HStack>
										<HStack bgColor={theme.colors.gray[500]} p="8px" width="25%" borderRadius={2}>
											<Switch.Root
												size="lg"
												disabled={!window.isSupervisor && isSubmitted && !edit}
												onCheckedChange={() =>
													dispatch({
														type: 'update_crew_member',
														payload: {
															field: 'lunch',
															index: cmIndex,
															value: !cm.lunch
														}
													})
												}
												checked={cm.lunch}
											>
												<Switch.HiddenInput />
												<Switch.Control />
											</Switch.Root>
											<Text as="label" fontSize={16}>
												Lunch
											</Text>
											<NumberInput.Root
												precision={2}
												value={cm.lunch_cost || '11.00'}
												onValueChange={e =>
													dispatch({
														type: 'update_crew_member',
														payload: {
															field: 'lunch_cost',
															index: cmIndex,
															value: e.value
														}
													})
												}
												disabled={isSubmitted && !edit}
											>
												<NumberInput.Input
													fontSize={16}
													bgColor="white"
													borderRadius="base"
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</NumberInput.Root>
										</HStack>
										<HStack bgColor={theme.colors.gray[500]} p="8px" width="25%" borderRadius={2}>
											<Switch.Root
												size="lg"
												disabled={!window.isSupervisor && isSubmitted && !edit}
												onCheckedChange={() =>
													dispatch({
														type: 'update_crew_member',
														payload: {
															field: 'dinner',
															index: cmIndex,
															value: !cm.dinner
														}
													})
												}
												checked={cm.dinner}
											>
												<Switch.HiddenInput />
												<Switch.Control />
											</Switch.Root>
											<Text as="label" fontSize={16}>
												Dinner
											</Text>
											<NumberInput.Root
												precision={2}
												value={cm.dinner_cost || '20.00'}
												onValueChange={e => {
													dispatch({
														type: 'update_crew_member',
														payload: {
															field: 'dinner_cost',
															index: cmIndex,
															value: e.value
														}
													});
												}}
												disabled={isSubmitted && !edit}
											>
												<NumberInput.Input
													fontSize={16}
													bgColor="white"
													borderRadius="base"
													_disabled={{
														opacity: 1,
														bgColor: theme.colors.gray[400],
														color: theme.colors.black
													}}
												/>
											</NumberInput.Root>
										</HStack>
									</HStack>
									<HStack height="50px">
										<Box width="25%" alignItems="center">
											<Text fontWeight="bold" fontSize={16}>
												Labor Code
											</Text>
										</Box>
										<Box width="25%" alignItems="center">
											<Text fontWeight="bold" fontSize={16}>
												Phase
											</Text>
										</Box>
										<Box width="25%" alignItems="center">
											<Text fontWeight="bold" fontSize={16}>
												Task
											</Text>
										</Box>
										<Box width="25%" alignItems="center">
											<Text fontWeight="bold" fontSize={16}>
												Hours
											</Text>
										</Box>
									</HStack>
									{/* Loop through all crew member owned time logs */}
									{Object.keys(cm.timelogs)
										// Filter crew member time logs by available time logs in project information
										// and by converted crew member data structure
										.filter(
											key =>
												cm.timelogs[key] &&
												Object.prototype.hasOwnProperty.call(cm.timelogs[key], 'key') &&
												state.fields.time_items?.some(timeItem => timeItem.key === key)
										)
										.map((key, tlIndex) => (
											<HStack
												py="8px"
												key={key}
												bg={tlIndex % 2 !== 0 ? theme.colors.gray[500] : theme.colors.white}
											>
												<Box width="25%" alignItems="center" pl="4px">
													<Text fontWeight="600" fontSize={16}>
														{cm.timelogs[key].code} - {cm.timelogs[key].label}
													</Text>
												</Box>
												<Box width="25%">
													<NativeSelect.Root size="sm">
														<NativeSelect.Field
															fontSize={16}
															placeholder=" "
															bgColor="inherit !important"
															borderRadius="base"
															value={cm.timelogs[key].phase_number}
															onChange={({ target }) => {
																dispatch({
																	type: 'set_time_data',
																	payload: {
																		index: cmIndex,
																		field: 'phase_number',
																		key,
																		value: target.value,
																		timeItems: handoffData.time_items
																	}
																});
																dispatch({
																	type: 'set_time_data',
																	payload: {
																		index: cmIndex,
																		field: 'task',
																		key,
																		value: '',
																		timeItems: handoffData.time_items
																	}
																});
															}}
															disabled={!window.isSupervisor && isSubmitted && !edit}
															_disabled={{
																opacity: 1,
																bgColor: theme.colors.gray[400],
																color: theme.colors.black
															}}
														>
															{projectPhases.map(({ phase_number }, _index) => (
																<option key={_index} value={phase_number}>
																	{phase_number}
																</option>
															))}
														</NativeSelect.Field>
														<NativeSelect.Indicator />
													</NativeSelect.Root>
												</Box>
												<Box width="25%">
													<Field.Root
														invalid={
															!(
																handoffData.project_type === 'OVERHEAD' ||
																cm.timelogs[key].task
															)
														}
													>
														<NativeSelect.Root size="sm">
															<NativeSelect.Field
																fontSize={16}
																placeholder=" "
																bgColor="inherit !important"
																borderRadius="base"
																value={cm.timelogs[key].task}
																onChange={({ target }) => {
																	dispatch({
																		type: 'set_time_data',
																		payload: {
																			index: cmIndex,
																			field: 'task',
																			key,
																			value: target.value,
																			timeItems: handoffData.time_items
																		}
																	});
																}}
																disabled={!window.isSupervisor && isSubmitted && !edit}
																_disabled={{
																	opacity: 1,
																	bgColor: theme.colors.gray[400],
																	color: theme.colors.black
																}}
															>
																{(
																	projectPhases.find(
																		({ phase_number }) =>
																			phase_number ===
																			cm.timelogs[key].phase_number
																	)?.tasks || []
																).map(({ number, name }, _index) => (
																	<option key={_index} value={number}>
																		{name}
																	</option>
																))}
															</NativeSelect.Field>
															<NativeSelect.Indicator />
														</NativeSelect.Root>
													</Field.Root>
												</Box>
												<Box width="25%">
													<NativeSelect.Root size="sm">
														<NativeSelect.Field
															fontSize={16}
															bgColor="inherit !important"
															borderRadius="base"
															value={cm.timelogs[key]?.hours || 0}
															onChange={({ target }) =>
																dispatch({
																	type: 'set_time_data',
																	payload: {
																		index: cmIndex,
																		field: 'hours',
																		key,
																		value: Number(target.value) || 0,
																		timeItems: handoffData.time_items
																	}
																})
															}
															disabled={
																(isSubmitted && !edit) ||
																(!state.fields.calc_disabled &&
																	(key === '110-1' ||
																		key === '100-1' ||
																		key === '102-1' ||
																		key === '103-1' ||
																		key === '603-1'))
															}
															_disabled={{
																opacity: 1,
																bgColor: theme.colors.gray[400],
																color: theme.colors.black
															}}
														>
															{/*	// If custom time log ticker value is non existent in options,*/}
															{/*	// add to time log hour ticker options as label/value object*/}
															{(!timeLogHours.some(
																({ value }) => value === cm.timelogs[key]?.hours
															)
																? [
																		{
																			label: formatTimeValue(
																				cm.timelogs[key]?.hours || 0
																			),
																			value: cm.timelogs[key]?.hours || 0
																		},
																		...timeLogHours
																	]
																: timeLogHours
															).map(({ label, value }, _index) => (
																<option key={_index} value={value}>
																	{label}
																</option>
															))}
														</NativeSelect.Field>
														<NativeSelect.Indicator />
													</NativeSelect.Root>
												</Box>
											</HStack>
										))}
									<Flex
										bgColor={theme.colors.blue[700]}
										p="8px"
										justify="space-between"
										align="center"
									>
										<Box display="flex" flexDirection="row">
											<Text color={theme.colors.white} fontWeight="bold" fontSize={16}>
												Total:{' '}
											</Text>
											<Text color={theme.colors.white} fontSize={16} fontWeight="900" pl="5px">
												{formatTimeValue(cm.total_hours)}
											</Text>
										</Box>
										<Box display="flex" flexDirection="row">
											<Text color={theme.colors.white} fontWeight="bold" fontSize={16}>
												Onsite Total:{' '}
											</Text>
											<Text color={theme.colors.white} fontSize={16} fontWeight="900" pl="5px">
												{formatTimeValue(cm.total_onsite_hours || 0)}
											</Text>
										</Box>
										<Box display="flex" flexDirection="row">
											<Text color={theme.colors.white} fontWeight="bold" fontSize={16}>
												Travel Total:{' '}
											</Text>
											<Text color={theme.colors.white} fontSize={16} fontWeight="900" pl="5px">
												{formatTimeValue(cm.timelogs['102-1']?.hours || 0)}
											</Text>
										</Box>
										<Box display="flex" flexDirection="row">
											<Text color={theme.colors.white} fontWeight="bold" fontSize={16}>
												Loadout Total:{' '}
											</Text>
											<Text color={theme.colors.white} fontSize={16} fontWeight="900" pl="5px">
												{formatTimeValue(cm.timelogs['100-1']?.hours || 0)}
											</Text>
										</Box>
									</Flex>
								</Box>
							))}
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
				<Accordion.Item value={1} mb={4} borderBottomWidth="1px">
					<Accordion.ItemTrigger>
						<Accordion.ItemIndicator />
						<Text fontSize={20} fontWeight="bold" color={theme.colors.blue[700]}>
							Equipment
						</Text>
					</Accordion.ItemTrigger>
					<Accordion.ItemContent p={0}>
						<Accordion.ItemBody>
							<Table.Root m="8px" variant="simple" style={{ tableLayout: 'fixed' }}>
								<Table.Header>
									<Table.Row>
										<Table.ColumnHeader>
											<Text p={2} fontSize="18px !important" fontWeight="bold">
												Unit Code
											</Text>
										</Table.ColumnHeader>
										<Table.ColumnHeader>
											<Text p={2} fontSize="18px !important" fontWeight="bold">
												Comment (Equipment #)
											</Text>
										</Table.ColumnHeader>
										<Table.ColumnHeader>
											<Text p={2} fontSize="18px !important" fontWeight="bold">
												Phase
											</Text>
										</Table.ColumnHeader>
										<Table.ColumnHeader colSpan={2}>
											<Text p={2} fontSize="18px !important" fontWeight="bold">
												Task
											</Text>
										</Table.ColumnHeader>
										<Table.ColumnHeader colSpan={2}>
											<Text p={2} fontSize="18px !important" fontWeight="bold">
												Quantity
											</Text>
										</Table.ColumnHeader>
										<Table.ColumnHeader>
											<Text p={2} fontSize="18px !important" fontWeight="bold">
												Rental
											</Text>
										</Table.ColumnHeader>
										<Table.ColumnHeader />
									</Table.Row>
								</Table.Header>
								<Table.Body>
									{state.fields.equipment.map((item, index) => (
										<Table.Row key={item.key}>
											<Table.Cell p={2}>
												<Text fontWeight={500} fontSize={16} mr="8px">
													{unitTable.find(({ unit }) => unit === item.unit)?.name ||
														item.unit}
												</Text>
											</Table.Cell>
											<Table.Cell p={2}>
												<Text fontWeight={500} fontSize={16}>
													{item.title}
												</Text>
											</Table.Cell>
											<Table.Cell p={2}>
												<NativeSelect.Root size="sm">
													<NativeSelect.Field
														fontSize={16}
														placeholder=" "
														onChange={({ target }) =>
															dispatch({
																type: 'update_equipment_item',
																payload: {
																	field: 'phase',
																	index,
																	value: target.value
																}
															})
														}
														value={item.phase}
														disabled={isSubmitted && !edit}
														_disabled={{
															opacity: 1,
															bgColor: theme.colors.gray[400],
															color: theme.colors.black
														}}
													>
														{projectPhases.map(({ phase_number }, _index) => (
															<option key={_index} value={phase_number}>
																{phase_number}
															</option>
														))}
													</NativeSelect.Field>
													<NativeSelect.Indicator />
												</NativeSelect.Root>
											</Table.Cell>
											<Table.Cell colSpan={2} p={2}>
												<Field.Root
													invalid={!(handoffData.project_type === 'OVERHEAD' || item.task)}
												>
													<NativeSelect.Root size="sm">
														<NativeSelect.Field
															fontSize={16}
															placeholder=" "
															onChange={({ target }) =>
																dispatch({
																	type: 'update_equipment_item',
																	payload: {
																		field: 'task',
																		index,
																		value: target.value
																	}
																})
															}
															value={item.task}
															disabled={isSubmitted && !edit}
															_disabled={{
																opacity: 1,
																bgColor: theme.colors.gray[400],
																color: theme.colors.black
															}}
														>
															{(
																projectPhases.find(
																	({ phase_number }) => phase_number === item.phase
																)?.tasks || []
															).map(({ number, name }, _index) => (
																<option key={_index} value={number}>
																	{name}
																</option>
															))}
														</NativeSelect.Field>
														<NativeSelect.Indicator />
													</NativeSelect.Root>
												</Field.Root>
											</Table.Cell>
											<Table.Cell colSpan={2} p={2}>
												<Box w="100%">
													<Text as="label" fontSize={16} pb="4px">
														{state.fields.equipment[index].measurement}
													</Text>
													<Field.Root
														invalid={
															!state.fields.equipment[index].quantity ||
															parseInt(state.fields.equipment[index].quantity) <= 0
														}
													>
														<NumberInput.Root
															value={state.fields.equipment[index].quantity || ''}
															onValueChange={e =>
																dispatch({
																	type: 'update_equipment_item',
																	payload: {
																		field: 'quantity',
																		index,
																		value: e.value
																	}
																})
															}
															disabled={
																(isSubmitted && !edit) ||
																state.fields.equipment[index].measurement === 'miles'
															}
														>
															<NumberInput.Input
																fontSize={16}
																p={0}
																pl={1}
																bgColor={theme.colors.gray[500]}
																borderRadius="base"
																_disabled={{
																	opacity: 1,
																	bgColor: theme.colors.gray[400],
																	color: theme.colors.black
																}}
															/>
														</NumberInput.Root>
													</Field.Root>
												</Box>
												{state.fields.equipment[index].measurement === 'miles' && (
													<Flex>
														<Box w="100%" mr="8px">
															<Text as="label" fontSize={16} pb="4px">
																Start
															</Text>
															<NumberInput.Root
																value={
																	state.fields.equipment[index].mileage_start || ''
																}
																onValueChange={e => {
																	dispatch({
																		type: 'update_equipment_item',
																		payload: {
																			field: 'mileage_start',
																			index,
																			value: e.value
																		}
																	});
																	dispatch({
																		type: 'update_equipment_item',
																		payload: {
																			field: 'quantity',
																			index,
																			value:
																				Number(
																					state.fields.equipment[index]
																						.mileage_end || 0
																				) - Number(e.value || 0)
																		}
																	});
																}}
																disabled={isSubmitted && !edit}
															>
																<NumberInput.Input
																	fontSize={16}
																	p={0}
																	pl={1}
																	bgColor={theme.colors.gray[500]}
																	borderRadius="base"
																	placeholder="mileage"
																	_disabled={{
																		opacity: 1,
																		bgColor: theme.colors.gray[400],
																		color: theme.colors.black
																	}}
																/>
															</NumberInput.Root>
														</Box>
														<Box w="100%">
															<Text as="label" fontSize={16} pb="4px">
																End
															</Text>
															<NumberInput.Root
																value={state.fields.equipment[index].mileage_end || ''}
																onValueChange={e => {
																	dispatch({
																		type: 'update_equipment_item',
																		payload: {
																			field: 'mileage_end',
																			index,
																			value: e.value
																		}
																	});
																	dispatch({
																		type: 'update_equipment_item',
																		payload: {
																			field: 'quantity',
																			index,
																			value:
																				Number(e.value || 0) -
																				Number(
																					state.fields.equipment[index]
																						.mileage_start || 0
																				)
																		}
																	});
																}}
																disabled={isSubmitted && !edit}
															>
																<NumberInput.Input
																	fontSize={16}
																	p={0}
																	pl={1}
																	bgColor={theme.colors.gray[500]}
																	borderRadius="base"
																	placeholder="mileage"
																	_disabled={{
																		opacity: 1,
																		bgColor: theme.colors.gray[400],
																		color: theme.colors.black
																	}}
																/>
															</NumberInput.Root>
														</Box>
													</Flex>
												)}
											</Table.Cell>
											<Table.Cell p={2}>
												<Icon
													style={{ marginLeft: 10 }}
													color={state.fields.equipment[index].rental ? 'green' : '#ccc'}
													size="xl"
												>
													<FaMoneyCheckAlt />
												</Icon>
											</Table.Cell>
											<Table.Cell p={2}>
												<CloseButton
													size="md"
													color={theme.colors.white}
													bgColor={theme.colors.red[500]}
													_hover={{ background: theme.colors.red[600] }}
													disabled={isSubmitted && !edit}
													onClick={() => setRemoveEquipmentItemDialogOpen(index)}
												/>
												<Dialog.Root
													role="alertdialog"
													initialFocusEl={removeEquipmentRefs.map.get(index)}
													onOpenChange={({ open }) => {
														if (!open) {
															setRemoveEquipmentItemDialogOpen(null);
														}
													}}
													open={removeEquipmentItemDialogOpen === index}
													placement="center"
												>
													<Portal>
														<Dialog.Backdrop />
														<Dialog.Positioner>
															<Dialog.Content opacity="1 !important">
																<Dialog.Header>
																	<Dialog.Title fontSize="lg" fontWeight="bold">
																		Confirm Delete
																	</Dialog.Title>
																</Dialog.Header>
																<Dialog.Body>
																	<p>Are you sure you want to remove this item?</p>
																</Dialog.Body>
																<Dialog.Footer>
																	<Dialog.ActionTrigger asChild>
																		<Button
																			size="lg"
																			ref={removeEquipmentRefs.ref(index)}
																			onClick={() =>
																				setRemoveEquipmentItemDialogOpen(null)
																			}
																		>
																			Cancel
																		</Button>
																	</Dialog.ActionTrigger>
																	<Button
																		colorPalette="red"
																		size="lg"
																		ml={3}
																		bg={theme.colors.red[500]}
																		color={theme.colors.white}
																		onClick={() => {
																			setRemoveEquipmentItemDialogOpen(null);
																			dispatch({
																				type: 'remove_equipment_item',
																				payload: { index }
																			});
																		}}
																	>
																		Delete Item
																	</Button>
																</Dialog.Footer>
																<Dialog.CloseTrigger asChild>
																	<CloseButton size="sm" />
																</Dialog.CloseTrigger>
															</Dialog.Content>
														</Dialog.Positioner>
													</Portal>
												</Dialog.Root>
											</Table.Cell>
										</Table.Row>
									))}
									<Table.Row>
										{loadoutList ? (
											<Table.Cell colSpan={5} p={2}>
												<Text as="label" fontSize={16} pb="4px">
													Comment (Equipment #)
												</Text>
												<NativeSelect.Root size="sm">
													<NativeSelect.Field
														fontSize={16}
														placeholder=" "
														onChange={({ target }) => {
															setLoadoutKey(target.value);

															const loadoutItem = loadoutListItems.find(
																({ key }) => key === target.value
															);

															if (loadoutItem) {
																setEquipmentItem({
																	name: loadoutItem.title,
																	rental: loadoutItem.rental,
																	billable: loadoutItem.billable,
																	unit: loadoutItem.unit
																});
															}
														}}
														value={loadoutKey}
														disabled={isSubmitted && !edit}
														_disabled={{
															opacity: 1,
															bgColor: theme.colors.gray[400],
															color: theme.colors.black
														}}
													>
														{loadoutListItems.map(({ key, title }, _index) => (
															<option key={_index} value={key}>
																{title}
															</option>
														))}
													</NativeSelect.Field>
													<NativeSelect.Indicator />
												</NativeSelect.Root>
											</Table.Cell>
										) : (
											<>
												<Table.Cell colSpan={2} p={2}>
													<Text as="label" fontSize={16} pb="4px">
														Unit Code
													</Text>
													<Field.Root invalid={!equipmentItem.unit}>
														<NativeSelect.Root size="sm">
															<NativeSelect.Field
																fontSize={16}
																bgColor={theme.colors.gray[500] + ' !important'}
																placeholder=" "
																onChange={({ target }) =>
																	setEquipmentItem(prevItem => ({
																		...prevItem,
																		unit: target.value
																	}))
																}
																value={equipmentItem.unit ?? ''}
																borderRadius="base"
																disabled={isSubmitted && !edit}
																_disabled={{
																	opacity: 1,
																	bgColor: theme.colors.gray[400],
																	color: theme.colors.black
																}}
															>
																{unitTable.map(({ unit, name }, _index) => (
																	<option key={_index} value={unit}>
																		{name}
																	</option>
																))}
															</NativeSelect.Field>
															<NativeSelect.Indicator />
														</NativeSelect.Root>
													</Field.Root>
												</Table.Cell>
												<Table.Cell colSpan={2} p={2}>
													<Text as="label" fontSize={16} pb="4px">
														Comment (Equipment #)
													</Text>
													<Input
														fontSize={16}
														bgColor={theme.colors.gray[500]}
														value={equipmentItem.name}
														onChange={event =>
															setEquipmentItem(prevItem => ({
																...prevItem,
																name: event.target.value
															}))
														}
														disabled={isSubmitted && !edit}
														_disabled={{
															opacity: 1,
															bgColor: theme.colors.gray[400],
															color: theme.colors.black
														}}
													/>
												</Table.Cell>
												<Table.Cell colSpan={2} p={2}>
													<HStack>
														<Switch.Root
															size="lg"
															onCheckedChange={() =>
																setEquipmentItem(prevItem => ({
																	...prevItem,
																	rental: !prevItem.rental
																}))
															}
															checked={equipmentItem.rental}
															disabled={isSubmitted && !edit}
														>
															<Switch.HiddenInput />
															<Switch.Control />
														</Switch.Root>
														<Text as="label" fontSize={16} pb="4px">
															Rental Item
														</Text>
													</HStack>
												</Table.Cell>
											</>
										)}
										<Table.Cell colSpan={2} p={2}>
											<HStack>
												<Switch.Root
													size="lg"
													disabled={isSubmitted && !edit}
													onCheckedChange={() => setLoadoutList(prevState => !prevState)}
													checked={loadoutList}
												>
													<Switch.HiddenInput />
													<Switch.Control />
												</Switch.Root>
												<Text as="label" fontSize={16} pb="4px">
													Loadout List
												</Text>
											</HStack>
										</Table.Cell>
										<Table.Cell colSpan={2} p={2}>
											<Button
												size="lg"
												disabled={
													(isSubmitted && !edit) ||
													(!loadoutList && !equipmentItem.unit) ||
													(loadoutList && !loadoutKey)
												}
												onClick={() => {
													if (equipmentItem.unit) {
														const measurement = unitTable.find(
															({ unit }) => equipmentItem.unit === unit
														)?.plural_label;

														dispatch({
															type: 'add_equipment_item',
															payload: {
																task: handoffData?.units?.task || '',
																measurement,
																phase:
																	handoffData?.units?.phase_number ||
																	handoffData?.phase_number ||
																	'',
																mileage_start: null,
																mileage_end: null,
																...equipmentItem
															}
														});
													}

													setEquipmentItem({
														name: '',
														rental: false,
														billable: true,
														unit: ''
													});
												}}
											>
												Add
											</Button>
										</Table.Cell>
									</Table.Row>
								</Table.Body>
							</Table.Root>
							<Spacer />
							<Text fontSize={24} as="label" p="8px" pb="4px">
								Subconsultants
							</Text>
							{state.fields.subconsultants.map(({ name, details }, index) => (
								<Flex key={index} align="flex-start">
									<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
										<Text as="label" fontSize={16} pb="4px">
											Company Name
										</Text>
										<Input
											fontSize={16}
											value={name}
											bgColor={theme.colors.white}
											onChange={({ target }) => {
												dispatch({
													type: 'update_subconsultant',
													payload: {
														field: 'name',
														value: target.value,
														index
													}
												});
											}}
											disabled={isSubmitted && !edit}
											_disabled={{
												opacity: 1,
												bgColor: theme.colors.gray[400],
												color: theme.colors.black
											}}
										/>
									</Box>
									<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
										<Text as="label" fontSize={16} pb="4px">
											Subconsultant Details
										</Text>
										<Textarea
											fontSize={16}
											bgColor={theme.colors.white}
											value={details}
											onChange={({ target }) => {
												dispatch({
													type: 'update_subconsultant',
													payload: {
														field: 'details',
														value: target.value,
														index
													}
												});
											}}
											disabled={isSubmitted && !edit}
											_disabled={{
												opacity: 1,
												bgColor: theme.colors.gray[400],
												color: theme.colors.black
											}}
										/>
									</Box>
									<CloseButton
										size="md"
										m="8px"
										color={theme.colors.white}
										bgColor={theme.colors.red[500]}
										_hover={{ background: theme.colors.red[600] }}
										disabled={isSubmitted && !edit}
										onClick={() => setRemoveSubConsultantDialogOpen(index)}
									/>
									<Dialog.Root
										role="alertdialog"
										initialFocusEl={removeSubconsultantRefs.map.get(index)}
										onOpenChange={({ open }) => {
											if (!open) {
												setRemoveSubConsultantDialogOpen(null);
											}
										}}
										open={removeSubConsultantDialogOpen === index}
										placement="center"
									>
										<Portal>
											<Dialog.Backdrop />
											<Dialog.Positioner>
												<Dialog.Content opacity="1 !important">
													<Dialog.Header>
														<Dialog.Title fontSize="lg" fontWeight="bold">
															Confirm Delete
														</Dialog.Title>
													</Dialog.Header>
													<Dialog.Body>
														<p>Are you sure you want to remove this item?</p>
													</Dialog.Body>
													<Dialog.Footer>
														<Dialog.ActionTrigger asChild>
															<Button
																size="lg"
																ref={removeSubconsultantRefs.ref(index)}
																onClick={() => setRemoveSubConsultantDialogOpen(null)}
															>
																Cancel
															</Button>
														</Dialog.ActionTrigger>
														<Button
															colorPalette="red"
															size="lg"
															ml={3}
															bg={theme.colors.red[500]}
															color={theme.colors.white}
															onClick={() => {
																setRemoveSubConsultantDialogOpen(null);
																dispatch({
																	type: 'remove_subconsultant',
																	payload: { index }
																});
															}}
														>
															Delete Item
														</Button>
													</Dialog.Footer>
													<Dialog.CloseTrigger asChild>
														<CloseButton size="sm" />
													</Dialog.CloseTrigger>
												</Dialog.Content>
											</Dialog.Positioner>
										</Portal>
									</Dialog.Root>
								</Flex>
							))}
							<Spacer />
							<Button
								size="lg"
								disabled={isSubmitted && !edit}
								m="8px"
								onClick={() => dispatch({ type: 'add_subconsultant' })}
								fontWeight="bold"
							>
								Add Subconsultant
							</Button>
						</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
			</Accordion.Root>
			<Flex w="100%" alignItems="center" justifyContent="space-between" px="8px">
				<Flex alignItems="center">
					<Icon
						color={currentPacket.packet_attachments?.photos?.length ? 'green' : 'orange'}
						size="xl"
						mr="12px"
					>
						<FaExclamationCircle />
					</Icon>
					<Heading fontSize={24} color={theme.colors.blue[700]} p="8px 0">
						Have you taken a site photo?
					</Heading>
				</Flex>
				<Button
					size="lg"
					color={theme.colors.white}
					bg={theme.colors.blue[700]}
					_hover={{ backgroundColor: theme.colors.blue[800] }}
					variant="solid"
				>
					Take Photo
				</Button>
			</Flex>
			<Heading fontSize={24} pt="24px" pb="12px" color={theme.colors.blue[700]} ml="8px">
				Signatures
			</Heading>
			<Flex>
				<Box w="100%" p="8px">
					<Text fontSize={16} as="label" pb="4px" pl="8px">
						Client Representative
					</Text>
					<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
						<Text as="label" fontSize={16} pb="4px">
							Print Name
						</Text>
						<Input
							fontSize={16}
							value={state.fields.client_name}
							onChange={({ target }) => {
								dispatch({
									type: 'update_field_value',
									payload: {
										fieldName: 'client_name',
										fieldValue: target.value
									}
								});
							}}
							disabled={isSubmitted && !edit}
							_disabled={{
								opacity: 1,
								bgColor: theme.colors.gray[400],
								color: theme.colors.black
							}}
							bgColor={theme.colors.white}
							borderRadius="base"
						/>
					</Box>
					<SignaturePad
						disabled={isSubmitted && !edit}
						src={
							state.fields.client_signature ||
							'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
						}
						onSign={signatureData =>
							dispatch({
								type: 'save_signature',
								payload: {
									signatureType: 'client',
									signatureData
								}
							})
						}
					/>
				</Box>
				<Box w="100%" p="8px">
					<Text fontSize={16} as="label" pb="4px" pl="8px">
						Supervisor
					</Text>
					<Box w="100%" p="8px" m="8px" bgColor={theme.colors.gray[500]} borderRadius="base">
						<Text as="label" fontSize={16} pb="4px">
							Print Name
						</Text>
						<Field.Root invalid={!state.fields.supervisor_name}>
							<Input
								fontSize={16}
								value={state.fields.supervisor_name}
								onChange={({ target }) => {
									dispatch({
										type: 'update_field_value',
										payload: {
											fieldName: 'supervisor_name',
											fieldValue: target.value
										}
									});
								}}
								disabled={isSubmitted && !edit}
								_disabled={{
									opacity: 1,
									bgColor: theme.colors.gray[400],
									color: theme.colors.black
								}}
								bgColor={theme.colors.white}
								borderRadius="base"
							/>
						</Field.Root>
					</Box>
					<Box borderBottom={state.fields.supervisor_signature ? 'none' : '2px solid red'}>
						<SignaturePad
							disabled={isSubmitted && !edit}
							src={
								state.fields.supervisor_signature ||
								'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
							}
							onSign={signatureData =>
								dispatch({
									type: 'save_signature',
									payload: {
										signatureType: 'supervisor',
										signatureData
									}
								})
							}
						/>
					</Box>
				</Box>
			</Flex>
		</>
	);
};
